import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Collapse, MenuList, Paper } from "@mui/material";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { TransferStepLabel } from "@/components/screens/Chat/components/TransferStepLabel";
import { IConversationTransferStep } from "@/interfaces/conversation-transfer.interfaces";
import { CURRENT_CHAT_INPUT_MESSAGES } from "@/messages/chat/current-chat-input.messages";
import NotificationItem from "@/mui/Items/NotificationItem";

interface CurrentChatPopOverProps {
  isOpen: boolean;
  anchorEl: (EventTarget & HTMLButtonElement) | null;
  onClose: () => void;
  canAssume: boolean;
  canFinish: boolean;
  chatInputEnabled: boolean;
  enableChatInput: () => void;
  disableChatInput: () => void;
  transferSteps: IConversationTransferStep[];
  onTransferToStep: (step: IConversationTransferStep) => void;
}

export function CurrentChatPopOver({
  isOpen,
  anchorEl,
  onClose,
  canAssume,
  canFinish,
  chatInputEnabled,
  enableChatInput,
  disableChatInput,
  transferSteps,
  onTransferToStep,
}: CurrentChatPopOverProps) {
  const { assumeControl, finishControl, transferStepMenu } = CURRENT_CHAT_INPUT_MESSAGES;
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [, setPositionTick] = useState(0);

  useEffect(() => {
    if (!isOpen) setIsTransferOpen(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !anchorEl) return undefined;

    const updateCoords = () => setPositionTick((tick) => tick + 1);

    window.addEventListener("resize", updateCoords);
    window.addEventListener("scroll", updateCoords, true);

    return () => {
      window.removeEventListener("resize", updateCoords);
      window.removeEventListener("scroll", updateCoords, true);
    };
  }, [isOpen, anchorEl]);

  if (!isOpen || !anchorEl || typeof document === "undefined") return null;

  const rect = anchorEl.getBoundingClientRect();
  const top = rect.bottom;
  const right = window.innerWidth - rect.right;

  return createPortal(
    <>
      <MDBox
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: (theme) => theme.zIndex.modal,
        }}
      />
      <Paper
        elevation={8}
        sx={(theme) => ({
          position: "fixed",
          top,
          right,
          zIndex: theme.zIndex.modal + 1,
          transform: "scale(var(--app-zoom))",
          transformOrigin: "top right",
          minWidth: 200,
          p: 1,
        })}
      >
        <MenuList autoFocus disablePadding onKeyDown={(event) => {
          if (event.key === "Escape") onClose();
        }}>
          {canFinish && (
            <NotificationItem
              onClick={() => {
                disableChatInput();
                onClose();
              }}
              icon={<PersonIcon />}
              title={finishControl}
            />
          )}
          {canAssume && (
            <NotificationItem
              onClick={() => {
                enableChatInput();
                onClose();
              }}
              icon={<PersonIcon />}
              title={assumeControl}
            />
          )}
          {transferSteps.length > 0 && (
            <>
              <NotificationItem
                onClick={(event) => {
                  event.preventDefault();
                  setIsTransferOpen((prev) => !prev);
                }}
                icon={<SwapHorizIcon />}
              >
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                  ml={1}
                >
                  <MDTypography variant="button" fontWeight="regular">
                    {transferStepMenu}
                  </MDTypography>
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      ml: 1,
                      transform: isTransferOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                </MDBox>
              </NotificationItem>
              <Collapse in={isTransferOpen} timeout="auto" unmountOnExit>
                <MDBox pl={1.5}>
                  {transferSteps.map((step) => (
                    <NotificationItem
                      key={`${step.workflowId}-${step.stepName}`}
                      disabled={!chatInputEnabled}
                      onClick={() => {
                        if (!chatInputEnabled) return;
                        onTransferToStep(step);
                        onClose();
                      }}
                      icon={<SmartToyOutlinedIcon />}
                    >
                      <TransferStepLabel step={step} />
                    </NotificationItem>
                  ))}
                </MDBox>
              </Collapse>
            </>
          )}
        </MenuList>
      </Paper>
    </>,
    document.body
  );
}
